/** XeAgents.ai**/
/**
 * Knowledge base folder paths — the one canonical form.
 *
 * Folder paths used to be normalized two opposite ways, and the disagreement was
 * the reason an agent could point at a folder the user had renamed or deleted:
 *
 *   - rag-knowledge-routes.normalizeFolderPath() ADDED a leading slash  -> "/Sales"
 *   - agent-routes and incoming-agent.getAgentConfigWithRAG STRIPPED it -> "Sales"
 *
 * So the Knowledge Base page wrote "/Sales" into knowledge_base.metadata.folderPath
 * and into knowledge_base_folders, while the agent routes compared against "Sales"
 * and matched nothing — the folder resolved to zero documents and the agent kept
 * whatever ID snapshot it already had.
 *
 * Case was a second, independent split: the folder LIST grouped paths
 * case-sensitively (so "/Sales" and "/sales" showed as two folders) while folder
 * DELETE matched with LOWER() on both sides (so deleting either one deleted both).
 *
 * Canonical form here is: leading slash, no trailing slash, interior whitespace
 * preserved, original case preserved for display. Comparison and de-duplication go
 * through folderPathKey(), which lowercases — matching what delete already did, so
 * no existing folder becomes unreachable.
 */

/** The root folder. Documents are not supposed to live here, but legacy rows do. */
export const ROOT_FOLDER_PATH = '/';

/**
 * Canonical display form of a folder path: "/Sales", "/Docs/2024", or "/" for root.
 *
 * Accepts every shape that exists in the database today — "Sales", "/Sales",
 * "/Sales/", "//Sales", "  Sales  " — and returns the same string for all of them.
 */
export function normalizeFolderPath(path: string | null | undefined): string {
  if (path === null || path === undefined) return ROOT_FOLDER_PATH;

  // Collapse repeated slashes anywhere, not just at the start: "//Sales//2024" has
  // been seen in rows written by the upload path before it normalized at all.
  const collapsed = String(path).trim().replace(/\/{2,}/g, '/');
  const trimmed = collapsed.replace(/\/+$/, ''); // drop trailing slashes

  if (trimmed === '' || trimmed === '/') return ROOT_FOLDER_PATH;

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/**
 * Comparison key for a folder path. Use this as a Map key or in an equality test —
 * never store it, because it destroys the case the user chose.
 */
export function folderPathKey(path: string | null | undefined): string {
  return normalizeFolderPath(path).toLowerCase();
}

/** True when two paths name the same folder, whatever form each is written in. */
export function folderPathsEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  return folderPathKey(a) === folderPathKey(b);
}

/**
 * Folders an agent can usefully be pointed at: those holding at least one document.
 *
 * An empty folder gives the agent nothing to answer from, so offering it only lets
 * someone pick a folder that looks configured and retrieves nothing. The Knowledge
 * Base page still lists empty folders (a folder is created before it is filled);
 * this is for agent pickers only. `selectedPath` is kept even when empty so an
 * agent already saved against it does not show a blank selection.
 */
export function foldersWithDocuments<T extends { path: string; count?: number }>(
  folders: readonly T[],
  selectedPath?: string | null,
): T[] {
  return folders.filter(
    (folder) => (folder.count ?? 0) > 0 || (!!selectedPath && folderPathsEqual(folder.path, selectedPath)),
  );
}

/** True for the root folder in any of its spellings ("", "/", null, undefined). */
export function isRootFolderPath(path: string | null | undefined): boolean {
  return normalizeFolderPath(path) === ROOT_FOLDER_PATH;
}

/**
 * Display label for a folder: "Sales" for "/Sales", "Root" for "/".
 * Only for UI — never feed the result back into a comparison.
 */
export function folderDisplayName(path: string | null | undefined): string {
  const normalized = normalizeFolderPath(path);
  return normalized === ROOT_FOLDER_PATH ? 'Root' : normalized.replace(/^\//, '');
}

/**
 * The folder a knowledge base row belongs to.
 *
 * There are three places a folder can be recorded and they can disagree:
 * the folder_path column, metadata.folderPath (JSON), and the
 * knowledge_base_folders table. Nearly all existing code reads metadata.folderPath,
 * so that stays authoritative here; folder_path is the fallback for rows written by
 * paths that only ever set the column.
 */
export function knowledgeItemFolderPath(item: {
  metadata?: unknown;
  folderPath?: string | null;
}): string {
  const fromMetadata = (item.metadata as { folderPath?: unknown } | null | undefined)?.folderPath;
  if (typeof fromMetadata === 'string' && fromMetadata.trim() !== '') {
    return normalizeFolderPath(fromMetadata);
  }
  return normalizeFolderPath(item.folderPath);
}
